/**
 * React hooks for managing Supabase users
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SupabaseUserService, SupabaseUser, UserFilters } from '../integrations/supabase/userService';

// Query keys for cache management
export const supabaseUserQueryKeys = {
  all: ['supabase-users'] as const,
  lists: () => [...supabaseUserQueryKeys.all, 'list'] as const,
  list: (filters?: UserFilters) => [...supabaseUserQueryKeys.lists(), { filters }] as const,
  detail: (id: string) => [...supabaseUserQueryKeys.all, 'detail', id] as const,
  profile: () => [...supabaseUserQueryKeys.all, 'profile'] as const,
};

/**
 * Hook to fetch users from Supabase with filters
 */
export const useSupabaseUsers = (filters?: UserFilters) => {
  return useQuery({
    queryKey: supabaseUserQueryKeys.list(filters),
    queryFn: () => SupabaseUserService.getUsers(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook to fetch a single user by ID
 */
export const useSupabaseUser = (id: string | null) => {
  return useQuery({
    queryKey: supabaseUserQueryKeys.detail(id || ''),
    queryFn: () => id ? SupabaseUserService.getUserById(id) : null,
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * Hook to get current user profile
 */
export const useCurrentUserProfile = () => {
  return useQuery({
    queryKey: supabaseUserQueryKeys.profile(),
    queryFn: () => SupabaseUserService.getCurrentUserProfile(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Important for auth state changes
  });
};

/**
 * Hook to link user with auth
 */
export const useLinkAuthUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, authUserId }: { email: string; authUserId: string }) =>
      SupabaseUserService.linkAuthUser(email, authUserId),
    onSuccess: () => {
      // Invalidate user queries after linking
      queryClient.invalidateQueries({ queryKey: supabaseUserQueryKeys.all });
    },
  });
};

/**
 * Hook to update user profile
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<SupabaseUser> }) =>
      SupabaseUserService.updateUser(id, updates),
    onSuccess: (updatedUser) => {
      // Update the cache with the new user data
      queryClient.setQueryData(
        supabaseUserQueryKeys.detail(updatedUser.id),
        updatedUser
      );
      // Invalidate lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: supabaseUserQueryKeys.lists() });
    },
  });
};

/**
 * Legacy hook that mimics the original useUsers but uses Supabase
 * This can be used as a drop-in replacement
 */
export const useUsersFromSupabase = (filters?: UserFilters) => {
  const { data: users = [], isLoading: loading, error, refetch } = useSupabaseUsers(filters);

  return {
    users: users.map(user => ({
      // Map Supabase user format to original format for compatibility
      ID: user.legacy_id || user.id,
      Email: user.email,
      CompID: user.comp_id,
      UserName: user.user_name,
      PhoneNumber: user.phone_number,
      IsAdmin: user.is_admin,
      IsBOD: user.is_bod,
      IsSales: user.is_sales,
      UGroup: user.u_group,
      Avatar: user.avatar,
      // Add Supabase-specific fields
      supabaseId: user.id,
      authUserId: user.auth_user_id,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
    })),
    loading,
    error: error?.message || null,
    refresh: refetch,
  };
};

/**
 * Hook for cache invalidation utilities
 */
export const useSupabaseUserCache = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: supabaseUserQueryKeys.all }),
    invalidateList: (filters?: UserFilters) =>
      queryClient.invalidateQueries({ queryKey: supabaseUserQueryKeys.list(filters) }),
    invalidateDetail: (userId: string) =>
      queryClient.invalidateQueries({ queryKey: supabaseUserQueryKeys.detail(userId) }),
    invalidateProfile: () =>
      queryClient.invalidateQueries({ queryKey: supabaseUserQueryKeys.profile() }),
  };
};